import isEmail from 'is-email-maybe';
import * as React from 'react';
import type { View } from 'react-native';

import {
    createPalette,
    flex,
    type PaletteDef,
    PalettePositionWidth,
    TagInput,
    type TagInputProps,
    type TagInputRef,
    usePaletteHandle,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { useDebouncedState } from '@gitbook/util-react-hooks';
import { assertNever } from '@gitbook/util-ts';

import { apiResource, readAPICache } from '../../state';
import { searchOrganizationMembers } from '../Search';
import { UserAvatar } from './UserAvatar';

export type EmailTag = { type: 'email'; email: string };
export type UserTag = { type: 'user'; displayName: string; user: string };
export type TeamTag = { type: 'team'; title: string; team: string; organization: string };

export type PermissionTag = EmailTag | UserTag | TeamTag;

type Props = Omit<
    TagInputProps<PermissionTag>,
    | 'onChangeValue'
    | 'ref'
    | 'wrapperRef'
    | 'text'
    | 'onChangeText'
    | 'onEnterTag'
    | 'renderTag'
    | 'tagDelimiters'
> & {
    organization: string;

    /**
     * If true, the input allows emails which will send
     * invites (as guest users).
     *
     * Defaults to true.
     */
    canInviteUsers?: boolean;

    /**
     * If true, guests (external members of the org) are shown.
     *
     * Defaults to true.
     */
    showGuests?: boolean;

    /**
     * If true, teams are shown.
     *
     * Defaults to true.
     */
    showTeams?: boolean;

    onChangeValue: (
        tags: PermissionTag[] | ((previousTags: PermissionTag[]) => PermissionTag[])
    ) => void;
};

export type UserTagInputRef = {
    /**
     * Reset the tag input.
     */
    reset: () => void;
};

/**
 * Generic input for searching users, teams or emails
 */
export const UserTagInput = React.forwardRef(function UserTagInputWithRef(
    props: Props,
    ref: React.Ref<UserTagInputRef>
): React.ReactElement {
    const {
        canInviteUsers = true,
        showTeams = true,
        showGuests = true,
        organization,
        value,
        onChangeValue,
        style: tagInputStyle,
        ...tagInputProps
    } = props;

    const [text, debouncedText, setText] = useDebouncedState('');
    const anchorRef = React.useRef<TagInputRef>(null);
    const wrapperRef = React.useRef<View>(null);
    const paletteHandle = usePaletteHandle(wrapperRef);

    //
    // Auto-focus when opening
    //
    React.useEffect(() => {
        anchorRef.current?.focus();
    }, []);

    //
    // Update autocomplete when user is typing
    //
    React.useEffect(() => {
        if (!text) {
            paletteHandle.close();
            return;
        }

        if (!paletteHandle.opened) {
            paletteHandle.open(
                createAutocompletePalette(
                    (item) => {
                        onChangeValue((prevTags) => [...prevTags, item]);
                        setText('');
                        paletteHandle.close();
                        anchorRef.current?.focus();
                    },
                    organization,
                    canInviteUsers,
                    showTeams,
                    showGuests
                )
            );
        }
        paletteHandle.typeQuery(debouncedText);
    }, [text, organization, debouncedText]);

    //
    // User pressed enter on a text.
    //
    const onEnterTag = () => {
        setText('');
    };

    React.useImperativeHandle(
        ref,
        () => ({
            reset: () => {
                setText('');
            },
        }),
        [setText]
    );

    const placeholder = (() => {
        if (canInviteUsers && showTeams) {
            return 'Add someone, a team, or an email';
        }

        if (canInviteUsers) {
            return 'Add someone or an email';
        }

        if (showTeams) {
            return 'Add someone or a team';
        }

        return 'Add someone';
    })();

    return (
        <TagInput
            ref={anchorRef}
            wrapperRef={wrapperRef}
            placeholder={placeholder}
            value={value}
            text={text}
            onChangeText={setText}
            onChangeValue={onChangeValue}
            onEnterTag={onEnterTag}
            renderTag={renderTag}
            tagDelimiters={['Enter', 'Tab']}
            style={[flex.flex1, tagInputStyle]}
            {...tagInputProps}
        />
    );
});

/**
 * Render a tag.
 */
function renderTag(item: PermissionTag): string {
    switch (item.type) {
        case 'email':
            return item.email;
        case 'user':
            return item.displayName;
        case 'team':
            return item.title;
        default:
            assertNever(item);
    }
}

/**
 * Create the palette to autocomplete users/teams.
 */
function createAutocompletePalette(
    pick: (item: PermissionTag) => any,
    organization: string,
    canInviteUsers: boolean,
    showTeams: boolean,
    showGuests: boolean
): PaletteDef {
    return createPalette({
        position: { width: PalettePositionWidth.ANCHOR },
        groups: [
            {
                key: 'email',
                commands: (palette) => {
                    const { text } = palette.query;
                    if (!canInviteUsers || !isEmail(text)) {
                        return [];
                    }

                    return [
                        {
                            key: text,
                            title: text,
                            icon: Icon.Email,
                            trigger: () => {
                                pick({ type: 'email', email: text });
                            },
                        },
                    ];
                },
            },
            {
                key: 'users',
                commands: (palette) => {
                    const { text } = palette.query;
                    const fetchedMembers = searchOrganizationMembers({
                        organizationId: organization,
                        query: text,

                        // Because the API currently doesn't allow searching and filtering by role,
                        // we need to filter out guests in the frontend below when `showGuests` is `false`.
                        // To increase the chances of having results to show, we fetch more than 10 members
                        // then we'll filter out guests and finally cap the list to 10.
                        limit: showGuests ? 10 : 50,
                    });

                    const members = showGuests
                        ? fetchedMembers
                        : fetchedMembers.filter((member) => member.role !== null).slice(0, 10);

                    return members.map((member) => {
                        const { user: memberUser } = member;
                        return {
                            key: memberUser.id,
                            icon: () => <UserAvatar user={memberUser} size="sm" tooltip={false} />,
                            title: memberUser.displayName,
                            subtitle: memberUser.email,
                            trigger: () => {
                                pick({
                                    type: 'user',
                                    displayName: memberUser.displayName,
                                    user: memberUser.id,
                                });
                            },
                        };
                    });
                },
            },
            {
                key: 'teams',
                commands: (palette) => {
                    if (!showTeams) {
                        return [];
                    }

                    const { text } = palette.query;
                    const { items } = readAPICache(
                        apiResource('listTeamsInOrganizationById', {
                            organizationId: organization,
                            limit: 10,
                            title: text,
                        })
                    );

                    return items.map((team) => ({
                        key: team.id,
                        icon: Icon.Users,
                        title: team.title,
                        trigger: () =>
                            pick({
                                type: 'team',
                                title: team.title,
                                organization,
                                team: team.id,
                            }),
                    }));
                },
            },
        ],
    });
}
